import {
  BarcodeType,
  type OrderShipmentBarcodeContent,
} from '~/modules/devices/types/barcode.type';
import { fetchOrderV2, fetchOrdersV2 } from '~/modules/orders/services/ordersV2.service';
import { type OrderShipmentV2 } from '~/modules/orders/types/ordersV2.type';
import { newRelic } from '~/utils/newRelic/newRelic';

import {
  type CreateScanBarcodeContentOptions,
  type FetchOrder,
  type ScanBarcodeContent,
  type ScanContextSource,
  type SearchOrders,
} from '../types/scanContext.type';
import { normalizeObjectId, normalizeOrderNumber } from './normalization.util';

/**
 * Recognized Shippo-generated barcode prefixes.
 */
const ORDER_SHIPMENT_BARCODE_PREFIXES = ['ORD'];

/**
 * Orders V2 searches across a variety of attributes. Multiple results
 * allows room to filter false matches. Best match expected to rise to top.
 */
const ORDER_NUMBER_SEARCH_RESULTS = 10;

/**
 * Resolve an order shipment to the most recent with matching rank. Higher batch
 * entry IDs are expected to represent newer IDs, matching Haven logic.
 */
const resolveOrderShipment = (
  orderShipments: OrderShipmentV2[],
  scannedOrderShipment: OrderShipmentV2,
) =>
  orderShipments
    .filter(
      ({ is_return, split_order_rank }) =>
        is_return === scannedOrderShipment.is_return &&
        split_order_rank === scannedOrderShipment.split_order_rank,
    )
    .reduce(
      (latest, orderShipment) => (orderShipment.id > latest.id ? orderShipment : latest),
      scannedOrderShipment,
    );

const toScanBarcodeContent = (
  orderObjectId: string,
  orderShipment: OrderShipmentV2,
): ScanBarcodeContent => ({
  orderObjectId,
  orderShipmentObjectId: orderShipment.object_id,
  shipmentId: String(orderShipment.id),
  ...(orderShipment.transaction_object_id && {
    transactionObjectId: orderShipment.transaction_object_id,
  }),
});

const createContentFromShippoBarcode = async ({
  barcodeContent,
  barcodeValue,
  correlationId,
  fetchOrder,
}: {
  barcodeContent: OrderShipmentBarcodeContent;
  fetchOrder: FetchOrder;
} & ScanContextSource): Promise<ScanBarcodeContent | undefined> => {
  const orderObjectId = normalizeObjectId(barcodeContent.order_id);
  const orderShipmentObjectId = normalizeObjectId(barcodeContent.shipment_id);

  const { order_shipments: orderShipments } = await fetchOrder(orderObjectId, {
    include_all_shipments: true,
  });
  const scannedOrderShipment = orderShipments.find(
    ({ object_id }) => object_id === orderShipmentObjectId,
  );

  if (!scannedOrderShipment) {
    newRelic.log('Failed to find Order Shipment', {
      customAttributes: {
        barcodeValue,
        correlationId,
        orderObjectId,
        orderShipmentObjectId,
      },
    });
    return undefined;
  }

  const orderShipment = resolveOrderShipment(orderShipments, scannedOrderShipment);

  if (orderShipment.object_id !== scannedOrderShipment.object_id) {
    newRelic.log('Scan resolved a replacement Order Shipment', {
      customAttributes: {
        barcodeValue,
        correlationId,
        orderObjectId,
        orderShipmentObjectId,
        resolvedOrderShipmentObjectId: orderShipment.object_id,
        splitOrderRank: orderShipment.split_order_rank,
      },
      level: 'INFO',
    });
  }

  return toScanBarcodeContent(orderObjectId, orderShipment);
};

const createContentFromOrderNumber = async ({
  barcodeValue,
  correlationId,
  searchOrders,
}: {
  searchOrders: SearchOrders;
} & ScanContextSource): Promise<ScanBarcodeContent | undefined> => {
  const orderNumber = normalizeOrderNumber(barcodeValue);
  if (!orderNumber) return undefined;

  const { results } = await searchOrders({
    q: barcodeValue,
    results: ORDER_NUMBER_SEARCH_RESULTS,
  });

  const matchedOrders = results.filter(
    ({ order_number }) => !!order_number && normalizeOrderNumber(order_number) === orderNumber,
  );

  if (matchedOrders.length !== 1) return undefined;

  const [matchedOrder] = matchedOrders;

  const orderShipments = matchedOrder.order_shipments.filter(({ is_return }) => !is_return);
  if (orderShipments.length !== 1) return undefined;

  const [orderShipment] = orderShipments;

  newRelic.log('Scan resolved an Order Shipment by Order Number', {
    customAttributes: {
      barcodeValue,
      correlationId,
      orderObjectId: matchedOrder.object_id,
      resolvedOrderShipmentObjectId: orderShipment.object_id,
    },
    level: 'INFO',
  });

  return toScanBarcodeContent(matchedOrder.object_id, orderShipment);
};

export const createScanBarcodeContent = async ({
  barcodeValue,
  correlationId,
  fetchOrder = fetchOrderV2,
  resolveBarcode,
  searchOrders = fetchOrdersV2,
}: CreateScanBarcodeContentOptions): Promise<ScanBarcodeContent | undefined> => {
  try {
    const barcode = ORDER_SHIPMENT_BARCODE_PREFIXES.some((prefix) =>
      barcodeValue.startsWith(prefix),
    )
      ? await resolveBarcode(barcodeValue)
      : null;

    if (barcode?.barcode_type === BarcodeType.OrderShipment) {
      return await createContentFromShippoBarcode({
        barcodeContent: barcode.barcode_content,
        barcodeValue,
        correlationId,
        fetchOrder,
      });
    }

    return await createContentFromOrderNumber({ barcodeValue, correlationId, searchOrders });
  } catch (error) {
    newRelic.noticeError('Failed to resolve scan barcode content', { correlationId, error });

    return undefined;
  }
};
